import React from "react";
import {
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import { DateRangePicker } from "@mui/lab";

import { useLazyMisplacedShipmentListQuery } from "store/services/reportApi";
import { useTablePagination } from "hooks";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#FEF9E7", textColor: "#B7950B" },
    CrossDocked: { backgroundColor: "#E8DAEF", textColor: "#8E44AD" },
    SendOut: { backgroundColor: "#FADBD8", textColor: "#E74C3C" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    Deconsolidation: { backgroundColor: "#E8ECEF", textColor: "#566573" }, // Added for Deconsolidation status
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const MisplacedShipmentListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const [dateRange, setDateRange] = React.useState({ fromDate: null, toDate: null });

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list"  
    },
    {
      title: "Misplaced Shipment Reports"
    }
  ];
  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => row.shipmentId,
    },
    {
      name: "Sender",
      selector: (row) => row?.customerId?.name || row?.vendor?.shopName || "N/A",
      cell: (row) => (
        <div>
          <Box sx={{ maxWidth: "600px", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row?.customerId?.name
              ? `CUS: ${row.customerId.name}`
              : row?.vendor?.shopName
                ? `VEN: ${row.vendor.shopName}`
                : "N/A"}
          </Box>
          {row?.customerId?.phone || row?.vendor?.phone ? (
            <span className="text-sm text-gray-500 dark:text-gray-300">
              {row?.customerId?.phone || row?.vendor?.phone}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.name || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">{row?.receiver?.phone || ""}</span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name || "N/A",
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            variant="outlined"
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Link to={`/app/crm/shipment/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Shipment" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchMisplacedShipments, { data: shipmentRes, isLoading, isFetching, error }] =
    useLazyMisplacedShipmentListQuery();

  React.useEffect(() => {
    fetchMisplacedShipments({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(dateRange.fromDate && dateRange.toDate && {
        fromDate: dateRange.fromDate.toISOString(),
        toDate: dateRange.toDate.toISOString(),
      }),
    });
  }, [fetchMisplacedShipments, currentPage, perPage, keyword, dateRange]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <TableFilter
          searchLabel="Search by Shipment ID, Sender, or Receiver"
          disabled={isLoading || isFetching}
          initialFilters={1}
        >
          <DateRangePicker
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            onChange={(newRange) => setDateRange(newRange)}
            disabled={isLoading || isFetching}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default MisplacedShipmentListScreen;